import { camelCase, get} from 'lodash'
import immutable from 'immutable';
import enums from './enum';
import util from './util';
import serviceGateway from './serviceGateway';
import store, { getModel, ModelCollection } from './store';
import userFactory from './models/userModel';
import employeeFactory from './models/employeeModel';
import router from './router';
import clientStorage from './clientStorage';
import socketClient from './socketClient';
import { Map as ImmutableMap } from 'immutable';
import globalRelay from './globalRelay';
import { IModal } from './models/modal';
import { IJobCost } from './models/jobCostModel';
import React from 'react';

const globalConfig = globalRelay.config!;

function _clearStore (keys = ['contractors', 'files', 'jobCosts', 'jobs', 'vendors']) {
	store().get('model').withMutations(m => {
		for(const key of keys) {
			m.set(key, m.get(key).clear());
		}
	});
}

function _setIdentity (user?, employee?) {
	let userChanged = true;

	store().get('viewState').withMutations(vs => {
		let viewState = vs;

		if (user) {
			const currentUser = viewState.get('currentUser');
			viewState = viewState.set('currentUser', userFactory(user));
			if (employee) {
				viewState = viewState.set('currentEmployee', employeeFactory(employee));
			}
			else {
				viewState.delete('currentEmployee');
			}

			userChanged = (currentUser && currentUser.key !== user.key);
		}
		else {
			socketClient.disconnect();
			viewState.delete('currentUser').delete('currentEmployee').setIn(['sidebar', 'expanded'], false);
		}
	});

	if (userChanged) {
		_clearStore();
	}
}

export const closeSidebar = () => {
	store().setIn(['viewState', 'sidebar', 'expanded'], false);
};

export const toggleSidebar = () => {
	const expanded = store().getIn(['viewState', 'sidebar', 'expanded']);

	store().setIn(['viewState', 'sidebar', 'expanded'], !expanded);
};

export const bootstrap = (params?) => {
	store().setIn(['viewState', 'bootstrapping'], true);

	return clientStorage.getItem('authToken')
		.then(result => {
			if (result) {
				socketClient.connect(result);
			}
			return serviceGateway.setAuthToken(result);
		})
		.then(result => {
			_setIdentity(result.user, result.employee);
			const bootstraps = [serviceGateway.fetchDomainEntities()];

			return Promise.all(bootstraps)
				.then(innerResult => {
					// be sure not to overwrite domain entities set by globalRelay
					let domainEntities = store().getIn(['model', 'domainEntities']) || {};

					domainEntities = {...domainEntities, ...innerResult[0].domainEntities};
					store().setIn(['model', 'domainEntities'], domainEntities);
				})
				.catch(function (err) {
					const key = util.generateGuid();

					pushAlert({
						key: key,
						type: 'error',
						message: err.message || 'Server Error',
						source: 'server',
						action: 'bootstrap',
						params: params,
						orgininalError: err,
						isCloseable: true
					});
				})
				.then(() => {
					return { currentUser: result.user };
				});
		})
		.finally(function () {
			store().setIn(['viewState', 'bootstrapping'], false);
		});
};

export const clearStore = params => {
	_clearStore(params);
};

export const pushModal = (params: IModal) => {
	store().getIn(['viewState', 'modals']).push(params);
};

export const deleteModal = ({ key }: { key: string }) => {
	const modals = store().getIn(['viewState', 'modals']);
	// filter is a Lazy function, toList forces it to execute
	const filtered = modals.filter(m => {
		return m.key !== key;
	}).toList();

	store().setIn(['viewState', 'modals'], filtered);
};

export const modalExists = ({ key }: { key: string }) => {
	const modals = store().getIn(['viewState', 'modals']);
	return modals.some(m => {
		return m.key === key;
	});
};

export const popModal = () => {
	store().getIn(['viewState', 'modals']).pop();
};

export const setHeader = params => {
	store().setIn(['viewState', 'headers', params.key], params.element);
};

export const deleteHeader = params => {
	const existing = store().getIn(['viewState', 'headers', params.key]);

	if (existing) {
		store().deleteIn(['viewState', 'headers', params.key]);
	}
};

export const setHeaderAction = ({key, element}: {key: string, element: React.ReactNode}) => {
	// if (params.push) {
	// 	params.bumped = store().getIn(['viewState', 'headerActions']).toObject();
	// }
	store().setIn(['viewState', 'headerActions', key], element);
};

export const deleteHeaderAction = ({key}: {key: string | number}) => {
	const existing = store().getIn(['viewState', 'headerActions', key]);

	if (existing) {
		store().deleteIn(['viewState', 'headerActions', key]);
	}
};

let _stashedHeaderActions;
export const stashHeaderActions = () => {
	_stashedHeaderActions = store().getIn(['viewState', 'headerActions']);

	store().setIn(['viewState', 'headerActions'], immutable.fromJS({}));
};

export const unstashHeaderActions = () => {
	_stashedHeaderActions = store().getIn(['viewState', 'headerActions']);

	if (_stashedHeaderActions) {
		store().setIn(['viewState', 'headerActions'], _stashedHeaderActions);
		_stashedHeaderActions = undefined;
	}
};

export const pushAlert = params => {
	const alert = {...params,
		key: params.key || util.generateGuid(),
		clear: function () {
			clearAlert(params);
		}
	};

	store().getIn(['viewState', 'alerts']).push(alert);
};

export const clearAlert = params => {
	const alerts = store().getIn(['viewState', 'alerts']);
	// filter is a Lazy function, toList forces it to execute
	const filtered = alerts.filter(a => {
		return a.key !== params.key;
	}).toList();

	store().setIn(['viewState', 'alerts'], filtered);
};

export const clearAlerts = () => {
	store().getIn(['viewState', 'alerts']).clear();
};

export const setTooltip = params => {
	store().setIn(['viewState', 'tooltip'], params);
};

export const setBanner = params => {
	store().setIn(['viewState', 'banner'], params);
};

export const login = params => {
	let user;
	let employee;
	let token;

	return serviceGateway.login(params)
		.then(result => {
			token = result.token;
			return serviceGateway.setAuthToken(result.token);
		})
		.then(result => {
			user = result.user;
			employee = result.employee;
			if (!user) {
				throw new Error('Login failed');
			}
		})
		.catch(() => { 
			pushAlert({ type: 'error', message: 'Incorrect User Name or Password. Please try again.', isCloseable: false });
		})
		.finally(() => {
			// will clear user and employee if user is missing
			_setIdentity(user, employee);
			globalRelay.triggerAppAuthChange({ currentUser: user });
			// reroute to next path
			if (user) {
				socketClient.connect(token);
				const nextPath = params.nextPathname || ('/' + (user && user.primaryModule));

				router.replaceHistory(nextPath);
			}
		});
};

export const logout = () => {
	return serviceGateway.setAuthToken()
		.then(() => {
			router.pushHistory('/login');
			_setIdentity();
			globalRelay.triggerAppAuthChange({ currentUser: undefined });
		});
};

export const requestPasswordReset = params => {
	return serviceGateway.requestPasswordReset(params);
};

export const resetPassword = params => {
	let user;
	let employee;

	return serviceGateway.resetPassword(params)
		.then(result => {
			return serviceGateway.setAuthToken(result.token);
		})
		.then(result => {
			user = result.user;
			employee = result.employee;
			if (!user) {
				throw new Error('Login failed');
			}
		})
		.catch(err => {
			pushAlert({ type: 'error', message: 'Your password could not be reset. Please try again.', isCloseable: false, orgininalError: err });
		})
		.finally(() => {
			// will clear user and employee if user is missing
			_setIdentity(user, employee);
			globalRelay.triggerAppAuthChange({ currentUser: user });
			// reroute to next path
			if (user) {
				const nextPath = params.nextPathname || ('/' + (user && user.primaryModule));

				router.replaceHistory(nextPath);
			}
		});
};

export const setGlobalSaveState = params => {
	if (typeof params.state !== 'undefined') {
		store().setIn(['viewState', 'globalSave', 'state'], params.state);
	}
};

export const setScrollContentKey = params => {
	if (typeof params.key !== 'undefined') {
		store().setIn(['viewState', 'scrollContentKey'], params.key);
	}
};

export const fetchConfig = params => {
	return serviceGateway.fetchConfig(params)
		.then(result => {
			store().setIn(['model', 'config', camelCase(params.name)], result);
		})
		.catch(err => {
			pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};

export const setSearchResults = params => {
	if (!params.key) {
		throw new Error('Search results must have a key property.');
	}

	store().setIn(['viewState', 'searchResults', params.key], params);
};

export const deleteSearchResults = params => {
	const existing = store().getIn(['viewState', 'searchResults', params.key]);

	if (existing) {
		store().deleteIn(['viewState', 'searchResults', params.key]);
	}
};

export const setPinnedStatState = params => {
	store().setIn(['viewState', 'pinnedStats', params.key], params.isPinned);
};

export const setPrintChildren = params => {
	store().setIn(['viewState', 'printChildren'], params || null);
};

export const clearPinnedStats = () => {
	store().setIn(['viewState', 'pinnedStats'], ImmutableMap());
};

export const formatConfigName = ({ companyVariant, jobKey = undefined }, { globalConfigPath, configFileBaseName } = { globalConfigPath: 'jobCostSettings.installation', configFileBaseName: 'installation'}) => {
	const variantConfig = globalConfig.withVariant(companyVariant);
	const settings = get(variantConfig, globalConfigPath); //.jobCostSettings[globalConfigKey];
	const storeSnapshot = store();
	const currentModule = storeSnapshot.getIn(['viewState', 'currentModule']);
	const job = getModel({ store: storeSnapshot, collection: ModelCollection.jobs, key: jobKey });
	const businessSegment = (job && job.businessSegment) || (currentModule === 'retail-sales' ? enums.businessSegments.Retail : currentModule === 'builder-sales' ? enums.businessSegments.Builder : enums.businessSegments.Unknown);
	const configVersion = settings.useBusinessSegement ? settings.configVersion[enums.businessSegments.getName(businessSegment).toLowerCase()] : settings.configVersion;

	const baseName = settings.useVariantConfig ? `${configFileBaseName}-${companyVariant}` : configFileBaseName;

	return settings.useBusinessSegement ? `${ baseName }-${enums.businessSegments.getName(businessSegment).toLowerCase()}-${configVersion}` : `${ baseName }-${configVersion}`;
};

export const getInstallationConfig = (jobCost) => {
	const installConfigName = jobCost.getSettingValue(enums.jobCostSettingGroups.General, 'installationConfigDefinition', undefined, formatConfigName(jobCost));
	const installationConfig = store().getIn(['model', 'config', camelCase(installConfigName)]);
	let fetchPromise: Promise<void>;

	if (installationConfig === undefined) {
		fetchPromise = fetchConfig({ name: installConfigName });
	}

	return { fetchPromise, installConfigName, installationConfig};
};

export const getInspectionConfig = (jobCost) => {
	const inspectionConfigName = jobCost.getSettingValue(enums.jobCostSettingGroups.General, 'inspectionConfigDefinition', undefined, formatConfigName(jobCost, { globalConfigPath: 'fieldSettings.inspection', configFileBaseName: 'inspection' }));
	const inspectionConfig = store().getIn(['model', 'config', camelCase(inspectionConfigName)]);
	let fetchPromise: Promise<void>;

	if (inspectionConfig === undefined) {
		fetchPromise = fetchConfig({ name: inspectionConfigName });
	}

	return { fetchPromise, inspectionConfigName, inspectionConfig};
};